import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Typography,
  Box,
  Divider
} from '@mui/material';
import apiService from '../services/api';

interface Plan {
  id: string;
  name: string;
  description?: string;
  pricePerSeat: number;
  currency: string;
  billingInterval: string;
}

interface Subscription {
  id: string;
  organizationId: string;
  planId: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';
  seatsPurchased: number;
  seatsUsed: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  canceledAt?: string;
  billingAmount?: number;
  paymentProviderId?: string;
  customerId?: string;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  plan: {
    id: string;
    name: string;
    description?: string;
    pricePerSeat: number;
    currency: string;
    billingInterval: string;
  };
}

interface EditSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription;
  onSuccess: () => void;
}

const EditSubscriptionDialog: React.FC<EditSubscriptionDialogProps> = ({
  open,
  onClose,
  subscription,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [formData, setFormData] = useState({
    status: subscription.status,
    planId: subscription.planId,
    seatsPurchased: subscription.seatsPurchased,
    seatsUsed: subscription.seatsUsed,
    currentPeriodStart: subscription.currentPeriodStart ? subscription.currentPeriodStart.split('T')[0] : '',
    currentPeriodEnd: subscription.currentPeriodEnd ? subscription.currentPeriodEnd.split('T')[0] : '',
    trialEnd: subscription.trialEnd ? subscription.trialEnd.split('T')[0] : '',
    canceledAt: subscription.canceledAt ? subscription.canceledAt.split('T')[0] : '',
    billingAmount: subscription.billingAmount || '',
    paymentProviderId: subscription.paymentProviderId || '',
    customerId: subscription.customerId || '',
  });

  useEffect(() => {
    if (open) {
      fetchPlans();
      // Reset form data when dialog opens
      setFormData({
        status: subscription.status,
        planId: subscription.planId,
        seatsPurchased: subscription.seatsPurchased,
        seatsUsed: subscription.seatsUsed,
        currentPeriodStart: subscription.currentPeriodStart ? subscription.currentPeriodStart.split('T')[0] : '',
        currentPeriodEnd: subscription.currentPeriodEnd ? subscription.currentPeriodEnd.split('T')[0] : '',
        trialEnd: subscription.trialEnd ? subscription.trialEnd.split('T')[0] : '',
        canceledAt: subscription.canceledAt ? subscription.canceledAt.split('T')[0] : '',
        billingAmount: subscription.billingAmount || '',
        paymentProviderId: subscription.paymentProviderId || '',
        customerId: subscription.customerId || '',
      });
      setError(null);
    }
  }, [open, subscription]);

  const fetchPlans = async () => {
    try {
      const response = await apiService.getPlans();
      setPlans(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate seats
      if (formData.seatsPurchased < 0) {
        setError('Seats purchased must be 0 or greater');
        setLoading(false);
        return;
      }
      if (formData.seatsUsed < 0) {
        setError('Seats used must be 0 or greater');
        setLoading(false);
        return;
      }
      if (formData.seatsUsed > formData.seatsPurchased) {
        setError('Seats used cannot exceed seats purchased');
        setLoading(false);
        return;
      }

      const updateData: any = {
        status: formData.status,
        planId: formData.planId,
        seatsPurchased: formData.seatsPurchased,
        seatsUsed: formData.seatsUsed,
      };

      // Only include dates if they have values
      if (formData.currentPeriodStart) {
        updateData.currentPeriodStart = new Date(formData.currentPeriodStart).toISOString();
      } else {
        updateData.currentPeriodStart = null;
      }

      if (formData.currentPeriodEnd) {
        updateData.currentPeriodEnd = new Date(formData.currentPeriodEnd).toISOString();
      } else {
        updateData.currentPeriodEnd = null;
      }

      if (formData.trialEnd) {
        updateData.trialEnd = new Date(formData.trialEnd).toISOString();
      } else {
        updateData.trialEnd = null;
      }

      if (formData.canceledAt) {
        updateData.canceledAt = new Date(formData.canceledAt).toISOString();
      } else {
        updateData.canceledAt = null;
      }

      if (formData.billingAmount) {
        updateData.billingAmount = parseFloat(formData.billingAmount.toString());
      }

      if (formData.paymentProviderId) {
        updateData.paymentProviderId = formData.paymentProviderId;
      }

      if (formData.customerId) {
        updateData.customerId = formData.customerId;
      }

      await apiService.updateSubscription(subscription.id, updateData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.id === formData.planId);
  const calculatedAmount = selectedPlan ? formData.seatsPurchased * selectedPlan.pricePerSeat : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Subscription</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Organization: <strong>{subscription.organization.name}</strong>
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
                <MenuItem value="past_due">Past Due</MenuItem>
                <MenuItem value="canceled">Canceled</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={formData.planId}
                onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                label="Plan"
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.pricePerSeat}/{plan.billingInterval}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Seats Purchased"
              type="number"
              value={formData.seatsPurchased}
              onChange={(e) => setFormData({ ...formData, seatsPurchased: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Seats Used"
              type="number"
              value={formData.seatsUsed}
              onChange={(e) => setFormData({ ...formData, seatsUsed: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0, max: formData.seatsPurchased }}
              helperText={`Available: ${formData.seatsPurchased - formData.seatsUsed}`}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Period Start"
              type="date"
              value={formData.currentPeriodStart}
              onChange={(e) => setFormData({ ...formData, currentPeriodStart: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Current Period End"
              type="date"
              value={formData.currentPeriodEnd}
              onChange={(e) => setFormData({ ...formData, currentPeriodEnd: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Trial End"
              type="date"
              value={formData.trialEnd}
              onChange={(e) => setFormData({ ...formData, trialEnd: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Canceled At"
              type="date"
              value={formData.canceledAt}
              onChange={(e) => setFormData({ ...formData, canceledAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Billing Amount"
              type="number"
              value={formData.billingAmount}
              onChange={(e) => setFormData({ ...formData, billingAmount: e.target.value })}
              inputProps={{ step: 0.01, min: 0 }}
              helperText={selectedPlan ? `Calculated: $${calculatedAmount.toFixed(2)} (${formData.seatsPurchased} × $${selectedPlan.pricePerSeat})` : ''}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Payment Provider ID"
              value={formData.paymentProviderId}
              onChange={(e) => setFormData({ ...formData, paymentProviderId: e.target.value })}
              placeholder="Stripe subscription ID"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Customer ID"
              value={formData.customerId}
              onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
              placeholder="Stripe customer ID"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Updating...' : 'Update Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSubscriptionDialog;










